import React, { useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./revenue.css";

import TopBar from "./TopBar";
import CompanyHeader from "./CompanyHeader";
import LeftNav from "./LeftNav";
import SectionHeader from "./SectionHeader";
import RootExplorer from "./RootExplorer";

import VisualExplorer from "./VisualCard/VisualExplorer";
import { buildRevenueTeardownData, normalizeRevenuePayload } from "./teardownAdapter";
import { useParams } from "react-router-dom";
import { fetchRevenueTeardown } from "../../../services/iqApiV2";
import RevenueNoData from "./FallBack/RevenueNoData";
import RevenueTeardownSkeleton from "./FallBack/RevenueTeardownSkeleton";


export default function RevenueTeardown({ initialData }) {
  const { clientName, competitorName } = useParams();
  const profileTo = useMemo(() => {
    if (!clientName || !competitorName) return null;
    return `/comp_profile/${clientName}/${competitorName}`;
  }, [clientName, competitorName]);

  // Hooks must always be top-level
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(null);

  const [activeDim, setActiveDim] = useState(null);
  const [view, setView] = useState("table");

  // Fetch once params change
  useEffect(() => {
    let mounted = true;
    async function run() {
      if (initialData) {
        setDoc(initialData);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const q = competitorName;
        if (!q) {
          setDoc(null);
          return;
        }
        const res = await fetchRevenueTeardown(q);
        if (mounted) setDoc(res);
      } catch (e) {
        if (mounted) setError(e?.message || "Failed to load revenue teardown");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    run();
    return () => { mounted = false; };
  }, [clientName, competitorName, initialData]);

  // Derive rows/meta
  const { rows: flatRows, meta, yearTotalsOverride, sources, baseFacts, assumptions } = useMemo(() => {
    if (!doc) return { rows: [], meta: null, sources: [], baseFacts: [], assumptions: [] };
    return normalizeRevenuePayload(doc);
  }, [doc]);

  const data = useMemo(() => {
    if (!flatRows?.length) return null;

    // Annual revenue override from meta (millions) if present
    const annualOverride =
      meta?.annual_revenue_millions ? meta.annual_revenue_millions : undefined;

    return buildRevenueTeardownData(
      flatRows,
      ["geo", "business", "product", "application", "customer", "channel"],
      "revenue_millions",
      undefined,
      {
       company: {
        name: meta?.company_name,
        annualRevenueOverride: annualOverride,
      },
        // (optional explicit fallback if you want the adapter’s new metaCompanyName guard)
        metaCompanyName: meta?.company_name,
        context: { fiscalYear: meta?.fiscal_year },
        defaultAvgGrowthPct: 15,                 // fallback if growth not provided
        rowGrowthFields: ["growthPct", "yoy_pct", "yoy"], // try these if present in rows
        totalsOverrideByYear: Object.fromEntries(yearTotalsOverride || []),
        sources,
        baseFacts,
        assumptions,
      }
    );
  }, [flatRows, meta, sources, baseFacts, assumptions,]);

  // Sync activeDim with data
  useEffect(() => {
    if (data?.dimensions?.length) {
      setActiveDim(data.dimensions[0].key);
    }
  }, [data]);

  if (loading) return <RevenueTeardownSkeleton />;
  if (error) return <RevenueNoData />;
  if (!data) return <RevenueNoData />

  // const [activeDim, setActiveDim] = useState(
  //   data?.activeDimension || data?.dimensions?.[0]?.key || null
  // );
  // const [view, setView] = useState("table");

  const active = data?.dimensions?.find(d => d.key === activeDim);
  const descMap = {
    geo: "Analyze revenue by geographic regions and markets",
    business: "Break down by business units and segments",
    product: "View revenue by products and service categories",
    customer: "Analyze customer types and specific accounts",
    application: "View by end-use applications and markets",
    channel: "Breakdown by distribution and sales channels",
  };
  const subtitle = descMap[active?.key] || "";

  return (
    <div className="container-fluid rt-container">
      {/* 1) Top bar */}
      <TopBar data={data} />

      {/* 2) Company header now lives inside the frame */}
      <div className="rt-frame">
        <div className="rt-frame-head">
          <CompanyHeader company={data.company} context={data.context} profileTo={profileTo} />
        </div>

        {/* Body: two columns, no gutters */}
        <div className="rt-frame-body row g-0">
          {/* Left nav */}
          <div className="col-12 col-md-3 rt-col-left">
            <LeftNav
              dimensions={data.dimensions || []}
              activeKey={activeDim}
              onSelect={setActiveDim}
            />
          </div>

          {/* Right panel */}
          <div className="col-12 col-md-9 rt-col-right">
            <SectionHeader
              title={active?.label || ""}
              subtitle={subtitle}
              view={view}
              onChangeView={setView}
            />
            {view === "table" ? (
              <RootExplorer data={data} activeDim={activeDim} />
            ) : (
              <VisualExplorer data={data} activeDim={activeDim} />
            )}
          </div>
        </div>
      </div>


    </div>
  );
}
